import { useMemo } from 'react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { formatMinutes } from '@/lib/format'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { resolveBillingTheme } from '../lib/theme'
import type { MinutesLedgerSummary } from '../types'

interface MinutesRemainingCardProps {
  ledger: MinutesLedgerSummary
  forecast: { daysRemaining: number; endDate: Date } | null
  /** When false (past month), the card shows "Minutes Used" + "Period closed" instead of remaining + forecast. */
  isCurrent: boolean
}

function formatDays(days: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(days)
}

function formatForecast(forecast: {
  daysRemaining: number
  endDate: Date
}): string {
  const dateFormat =
    forecast.endDate.getFullYear() === new Date().getFullYear()
      ? 'MMM d'
      : 'MMM d, yyyy'
  return `≈ ${formatDays(forecast.daysRemaining)} days · runs out ${format(forecast.endDate, dateFormat)}`
}

export function MinutesRemainingCard({
  ledger,
  forecast,
  isCurrent,
}: MinutesRemainingCardProps) {
  const total = ledger.startMinutes + ledger.addedMinutes
  const percentLeft = useMemo(() => {
    if (total <= 0) return 0
    // Clamp to [0, 100] — remainingMinutes can be negative on overage, which
    // would otherwise produce "-3% of N left" text and an invalid `width: -3%`
    // on the progress bar. The big number above the bar still shows the true
    // (possibly negative) remaining count.
    return Math.max(
      0,
      Math.min(100, Math.round((ledger.remainingMinutes / total) * 100))
    )
  }, [ledger.remainingMinutes, total])
  const theme = useMemo(
    () => resolveBillingTheme(isCurrent, forecast, ledger.remainingMinutes),
    [isCurrent, forecast, ledger.remainingMinutes]
  )

  if (!isCurrent) {
    return (
      <div
        className={cn('rounded-lg p-6 border-2 border-dashed', theme.surface)}>
        <div
          className={cn(
            'text-xs font-semibold tracking-widest uppercase mb-3',
            theme.label
          )}>
          Minutes Used
        </div>
        <div
          className={cn('text-5xl font-bold mb-2 leading-none', theme.heading)}>
          {formatMinutes(ledger.usedMinutes)}
        </div>
        <div className={cn('text-sm italic', theme.body)}>Period closed</div>
      </div>
    )
  }

  return (
    <div className={cn('rounded-lg p-6 border-2 border-dashed', theme.surface)}>
      <div
        className={cn(
          'text-xs font-semibold tracking-widest uppercase mb-3',
          theme.label
        )}>
        Minutes Remaining
      </div>
      <div
        className={cn('text-5xl font-bold mb-2 leading-none', theme.heading)}>
        {formatMinutes(ledger.remainingMinutes)}
      </div>
      <div className={cn('text-sm italic mb-5', theme.body)}>
        {forecast ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help underline decoration-dotted underline-offset-4">
                  {formatForecast(forecast)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                At your current pace, remaining minutes will last about this
                long. Forecast updates as usage continues.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : ledger.usedMinutes > 0 ? (
          `${formatMinutes(ledger.usedMinutes)} minutes used this period`
        ) : (
          'No usage yet this period'
        )}
      </div>
      <div
        className={cn(
          'w-full rounded-full h-2 overflow-hidden mb-2',
          theme.trackBg
        )}>
        <div
          className={cn(
            'h-2 rounded-full motion-safe:transition-[width] duration-fast ease-smooth-out',
            theme.trackFill
          )}
          style={{ width: `${percentLeft}%` }}
        />
      </div>
      <div className={cn('flex justify-between text-xs', theme.axis)}>
        <span>0</span>
        <span>
          {percentLeft}% of {formatMinutes(total)} left
        </span>
        <span>{formatMinutes(total)}</span>
      </div>
    </div>
  )
}
